import Text from '@creator/ui/components/Text/Text';
import { convertFirebaseTimestampToDate, formatDate } from '@creator/ui/utils/date-utils';
import { cn } from '@creator/ui/utils/ui';
import Link from '@src/basic-components/Link/Link';
import UserDisplayNameWithLink from '@src/basic-components/UserDisplayName/UserDisplayNameWithLink';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { usePost } from '@src/store/posts/hooks/usePost';
import type { FC } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PinnedIndicator from './PinnedIndicator/PinnedIndicator';

export interface PostBylineProps {
    tokenName: string;
    postId: string;
    className?: string;
}

const PostByline: FC<PostBylineProps> = props => {
    const { className = '', tokenName, postId } = props;
    const post = usePost(postId);

    const { t } = useTranslation();

    function getPostCreatedTime() {
        if (!post) return '';
        const { createdAt } = post;
        const _date = convertFirebaseTimestampToDate(createdAt);
        return formatDate(_date);
    }

    function getPostUpdatedTime() {
        if (!post) return '';
        const { updatedAt } = post;
        if (!updatedAt) return '';
        const _date = convertFirebaseTimestampToDate(updatedAt);
        return formatDate(_date);
    }

    function renderDivider() {
        return <div className="w-[1px] bg-current h-3 shrink-0" />;
    }

    function renderPartnerLabel() {
        if (!post) return null;
        const { createdByWhitelistedUser } = post;
        if (!createdByWhitelistedUser) return null;

        return (
            <Fragment>
                {renderDivider()}
                <Link to={`/${ensureTokenUrl(tokenName)}/tab/holders?type=partner`} className="font-medium text-primary-400 link">
                    <Text className="text-primary-400 link">{t('postCard.partnerLabel')}</Text>
                </Link>
            </Fragment>
        );
    }

    function renderUserDisplayNameWithLink() {
        return (
            <UserDisplayNameWithLink
                slugUrl={post?.publisherSlugUrl}
                tokenName={tokenName}
                profileName={post?.publisherProfileName}
                displayName={post?.publisherName || ''}
                userDisplayNameProps={{ className: '' }}
            />
        );
    }

    function renderPinnedIndicator() {
        if (!post?.isPinned) return null;

        return (
            <>
                {renderDivider()}
                <PinnedIndicator />
            </>
        );
    }

    return (
        <div className={cn('flex items-center h-3 space-x-2 text-xs', className)}>
            {renderUserDisplayNameWithLink()}
            {renderDivider()}
            <Text data-ssr="post-date" className="">
                {getPostCreatedTime()}
            </Text>
            {renderPartnerLabel()}
            {renderPinnedIndicator()}
        </div>
    );
};

export default PostByline;
